import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'

import styled, { useTheme } from 'styled-components'
import { v4 as uuid } from 'uuid'

import { TextSize, getFontSize, getLineHeight } from 'src/components/Typography/Text'

import { useMergeRefs } from '../../../hooks/use-merge-refs'
import { Size } from '../../types'
import { InputStyleProps, InputStyles } from '../../utils'
import FormControl from '../FormControl/FormControl'
import { FormControlProps } from '../FormControl/util'

const BorderWidthPx = 1
const getVerticalPaddingPx = (size: Size) => (size === 'sm' ? 4 : 8)
const getPadding = (size: Size) => `${getVerticalPaddingPx(size)}px 12px`
const StyledTextArea = styled.textarea<
  InputStyleProps & {
    $size: Size
    $resize: React.CSSProperties['resize']
  }
>`
  background-color: ${({ theme }) => theme.colorWhite};
  border-radius: 6px;
  border: solid ${BorderWidthPx}px ${({ theme }) => theme.colorBorder};
  box-sizing: border-box;
  color: ${({ theme }) => theme.colorText};
  padding: ${({ $size }) => getPadding($size)};
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${getFontSize(TextSize.Base)}px;
  line-height: ${getLineHeight(TextSize.Base)}px;
  resize: ${({ $resize }) => $resize ?? 'vertical'};
  width: 100%;

  ${InputStyles}
`

type Props = FormControlProps & {
  autoFocus?: boolean
  isDisabled?: boolean
  maxLength?: number
  maxRows?: number
  minRows?: number
  onBlur?: () => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
  onValueChange: (value: string) => void
  placeholder?: string
  resize?: React.CSSProperties['resize']
  size?: Size
  value: string
}
const TextArea = forwardRef<HTMLTextAreaElement, Props>(
  (
    {
      ariaLabel,
      caption,
      autoFocus,
      errorMessage,
      id: propId,
      isDisabled,
      isInvalid,
      isRequired,
      label,
      maxLength,
      maxRows = Infinity,
      minRows = 3,
      onBlur,
      onKeyDown,
      onValueChange,
      placeholder,
      resize = 'none',
      hideRequiredAsterisk,
      size = 'md',
      tooltip,
      value,
      width = '100%',
    }: Props,
    ref
  ) => {
    const [id] = useState(propId ?? uuid())
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onValueChange(e.target.value ?? '')
      },
      [onValueChange]
    )

    const innerRef = useRef<HTMLTextAreaElement>(null)
    const mergedRef = useMergeRefs(innerRef, ref)

    const theme = useTheme()
    const fontFamily = theme.fontFamily
    const fontSize = getFontSize(TextSize.Base)
    const lineHeight = getLineHeight(TextSize.Base)

    const controlledValue = value ?? '' // controlled input values cannot be null or undefined

    const resizeTextArea = useCallback(() => {
      // Implementation inspired by https://github.com/react-component/textarea/blob/master/src/calculateNodeHeight.tsx
      const el = innerRef.current
      if (!el) {
        return
      }

      // Inject a hidden element into the DOM to compute the number of lines without affecting the displayed textarea
      const hiddenEl = document.createElement('textarea')
      const borderPx = BorderWidthPx * 2
      const paddingPx = getVerticalPaddingPx(size) * 2
      hiddenEl.setAttribute('tab-index', '-1')
      hiddenEl.setAttribute('aria-hidden', 'true')
      hiddenEl.rows = 1
      hiddenEl.style.border = `solid ${BorderWidthPx}px`
      hiddenEl.style.boxSizing = 'border-box'
      hiddenEl.style.resize = resize
      hiddenEl.style.fontFamily = `${fontFamily}`
      hiddenEl.style.fontSize = `${fontSize}px`
      hiddenEl.style.lineHeight = `${lineHeight}px`
      hiddenEl.style.padding = getPadding(size)
      hiddenEl.style.width = `${el.getBoundingClientRect().width}px`
      hiddenEl.value = controlledValue
      document.body.appendChild(hiddenEl)

      // Perform calculations
      const numberOfLines = (hiddenEl.scrollHeight - paddingPx) / lineHeight
      const newRows = Math.min(Math.max(numberOfLines, minRows), maxRows)

      // Clean up hidden element
      document.body.removeChild(hiddenEl)

      // Assign computed changes to visible element
      el.rows = newRows
      el.style.height = `${newRows * lineHeight + borderPx + paddingPx}px`
    }, [controlledValue, fontFamily, fontSize, lineHeight, maxRows, minRows, resize, size])

    // Resize TextArea whenever value is updated
    useEffect(() => {
      resizeTextArea()
    }, [resizeTextArea, value])

    // Resize TextArea whenever element is externally resized
    useEffect(() => {
      const el = innerRef.current
      const observer = new ResizeObserver(() => resizeTextArea())
      if (el) {
        observer.observe(el)
      }
      return () => observer.disconnect()
    }, [resizeTextArea])

    return (
      <FormControl
        caption={caption}
        errorMessage={errorMessage}
        id={id}
        isInvalid={isInvalid}
        isRequired={isRequired}
        label={label}
        hideRequiredAsterisk={hideRequiredAsterisk}
        tooltip={tooltip}
        width={width}
      >
        {({ ariaDescribedBy, ariaInvalid, ariaLive }) => (
          <StyledTextArea
            autoFocus={autoFocus}
            $isDisabled={!!isDisabled}
            $isInvalid={!!isInvalid}
            $resize={resize}
            $size={size}
            aria-describedby={ariaDescribedBy}
            aria-invalid={ariaInvalid}
            aria-label={ariaLabel}
            aria-live={ariaLive}
            disabled={isDisabled}
            id={id}
            maxLength={maxLength}
            onBlur={onBlur}
            onChange={handleChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            ref={mergedRef}
            value={controlledValue}
          />
        )}
      </FormControl>
    )
  }
)

export default TextArea
